import {
  computed,
  onScopeDispose,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue'

type Identifiable = { id: string }

/** Milliseconds a drag suppresses the click the browser fires on the dragged link. */
const CLICK_SUPPRESSION_MS = 150

const arrayMove = <T>(items: T[], from: number, to: number) => {
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/**
 * Native HTML5 drag-to-reorder for a sidebar list whose rows are also links.
 *
 * The list is mirrored into local state so the order can be previewed while dragging and
 * committed optimistically on drop, rolling back if the server rejects it. Refetches that
 * land mid-drag are ignored — otherwise the row would jump out from under the cursor.
 *
 * @param items       Source order (a query result); adopted whenever no drag is active.
 * @param rowSelector Selector of the row element to clone for the drag image.
 * @param onCommit    Persists the dropped order. Returning a promise that rejects
 *                    restores the previous order; returning nothing leaves the caller
 *                    responsible for rollback (e.g. refetching the source).
 */
export function useDragReorderList<T extends Identifiable>({
  items,
  rowSelector,
  onCommit,
}: {
  items: Ref<T[]> | ComputedRef<T[]>
  rowSelector: string

  onCommit: (orderedIds: string[], orderedItems: T[]) => unknown
}) {
  const orderedItems = ref<T[]>([]) as Ref<T[]>
  const livePreviewOrder = ref<T[]>([]) as Ref<T[]>
  const isDragging = ref(false)
  const draggingId = ref<string | null>(null)
  const hoverId = ref<string | null>(null)

  /** After a drag the browser may still emit a click on the link; skip navigation once. */
  const preventNextClick = ref(false)

  watch(
    items,
    (nextItems) => {
      if (isDragging.value) return
      orderedItems.value = nextItems ? [...nextItems] : []
    },
    { immediate: true, deep: true },
  )

  const displayItems = computed(() =>
    isDragging.value && livePreviewOrder.value.length
      ? livePreviewOrder.value
      : orderedItems.value,
  )

  const onDragStart = (event: DragEvent, id: string) => {
    isDragging.value = true
    livePreviewOrder.value = [...orderedItems.value]
    draggingId.value = id
    hoverId.value = id

    const row = (event.target as HTMLElement | null)?.closest(
      rowSelector,
    ) as HTMLElement | null

    if (!row || !event.dataTransfer) return

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', id)

    // The row is cloned into the body purely to act as the drag image: dragging the live
    // node would drag it at its post-`dragstart` (dimmed) opacity.
    const clone = row.cloneNode(true) as HTMLElement
    clone.style.width = `${row.offsetWidth}px`
    clone.style.boxSizing = 'border-box'
    clone.style.opacity = '0.95'
    clone.style.pointerEvents = 'none'
    document.body.appendChild(clone)
    const rect = row.getBoundingClientRect()
    event.dataTransfer.setDragImage(
      clone,
      Math.min(event.clientX - rect.left, rect.width),
      Math.min(event.clientY - rect.top, rect.height),
    )
    requestAnimationFrame(() => clone.remove())
  }

  const onDragOver = (event: DragEvent, id: string) => {
    if (!isDragging.value || !draggingId.value) return
    event.preventDefault()
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'

    // dragover repeats on the same row; only reorder when the hover target changes.
    if (hoverId.value === id) return
    hoverId.value = id

    const list = livePreviewOrder.value
    const from = list.findIndex((item) => item.id === draggingId.value)
    const to = list.findIndex((item) => item.id === id)
    if (from < 0 || to < 0 || from === to) return

    livePreviewOrder.value = arrayMove(list, from, to)
  }

  // `window.setTimeout` (not the Node global) — its handle is a plain number.
  let clickSuppressionTimer: number | undefined
  onScopeDispose(() => {
    if (clickSuppressionTimer !== undefined) clearTimeout(clickSuppressionTimer)
  })

  const onDragEnd = () => {
    preventNextClick.value = true
    if (clickSuppressionTimer !== undefined) clearTimeout(clickSuppressionTimer)
    clickSuppressionTimer = window.setTimeout(() => {
      clickSuppressionTimer = undefined
      preventNextClick.value = false
    }, CLICK_SUPPRESSION_MS)
    isDragging.value = false
    livePreviewOrder.value = []
    draggingId.value = null
    hoverId.value = null
  }

  /** Bind to the row link's `click`: swallows the click that follows a drag. */
  const onRowClick = (event: MouseEvent) => {
    if (!preventNextClick.value) return
    event.preventDefault()
    preventNextClick.value = false
  }

  const onDrop = async (event: DragEvent) => {
    event.preventDefault()
    if (!isDragging.value) return

    const next = [...displayItems.value]
    const previous = [...orderedItems.value]
    const isUnchanged =
      next.length === previous.length &&
      next.every((item, index) => item.id === previous[index].id)

    onDragEnd()

    if (isUnchanged) return

    orderedItems.value = next

    try {
      await onCommit(
        next.map((item) => item.id),
        next,
      )
    } catch {
      orderedItems.value = previous
    }
  }

  /**
   * Keyboard equivalent of a drag, bound to each row's `keydown`. Native HTML5 drag and
   * drop is pointer-only, which would leave the order unreachable without a mouse, so
   * `Alt` + `ArrowUp` / `ArrowDown` moves the focused row one step and commits it.
   *
   * `Alt` keeps the plain arrows free for the browser's own list and caret navigation,
   * and the handler is a no-op for every other key so typing inside a row still works.
   */
  const onRowKeydown = async (event: KeyboardEvent, id: string) => {
    if (!event.altKey) return
    const direction =
      event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0
    if (!direction) return

    const from = orderedItems.value.findIndex((item) => item.id === id)
    const to = from + direction
    if (from < 0 || to < 0 || to >= orderedItems.value.length) return

    event.preventDefault()

    const previous = [...orderedItems.value]
    const next = arrayMove(previous, from, to)
    orderedItems.value = next

    try {
      await onCommit(
        next.map((item) => item.id),
        next,
      )
    } catch {
      orderedItems.value = previous
    }
  }

  /** True while this row is the drop target (for the row's hover styling). */
  const isDropTarget = (id: string) => isDragging.value && hoverId.value === id
  /** True for the row being dragged (rendered dimmed in place). */
  const isDragSource = (id: string) =>
    isDragging.value && draggingId.value === id

  return {
    displayItems,
    isDragging,
    isDropTarget,
    isDragSource,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDrop,
    onRowClick,
    onRowKeydown,
  }
}
